/**
 * Integrytas Group - Performance Diagnostics Engine (PDE)
 * web_vitals - dependency-free browser performance reporting.
 *
 * Captures Navigation Timing (TTFB, DOM interactive, full page load) once per
 * page load and posts it to pde_log_web_vital, which stamps the authenticated
 * user server-side. Feeds the "Client web-vitals" tier of the diagnostics
 * dashboard. Uses only the built-in Performance API (no npm dependency).
 */

import { apiDb } from './api_client';

let _reported = false;

function post(metricName: string, metricValue: number, pageCode?: string | null): void {
  void apiDb.rpc('pde_log_web_vital', {
    p_payload: {
      metric_name:  metricName,
      metric_value: metricValue,
      page_code:    pageCode ?? null,
      route:        typeof location !== 'undefined' ? location.pathname : null,
      user_agent:   typeof navigator !== 'undefined' ? navigator.userAgent : null,
    },
  });
}

/**
 * Report navigation-timing metrics once. Safe no-op when the Performance API is
 * unavailable or already reported. Call after a session exists so the RPC (which
 * is granted to authenticated) succeeds.
 */
export function reportNavigationTiming(pageCode?: string | null): void {
  if (_reported || typeof performance === 'undefined' || typeof document === 'undefined') return;

  const emit = () => {
    if (_reported) return;
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return;
    _reported = true;
    if (nav.responseStart  > 0) post('TTFB',            Math.round(nav.responseStart));
    if (nav.domInteractive > 0) post('DOM_INTERACTIVE', Math.round(nav.domInteractive));
    const load = nav.loadEventEnd || nav.duration;
    if (load > 0) post('PAGE_LOAD', Math.round(load), pageCode);
  };

  if (document.readyState === 'complete') emit();
  else window.addEventListener('load', () => setTimeout(emit, 0), { once: true });
}
